import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Mail } from 'lucide-react';
import { calculationAPI } from '../lib/api';
import DdgCaptcha from './DdgCaptcha';
import { DDG_CAPTCHA_REQUIRED_MESSAGE } from '../lib/ddgCaptcha';
import PersonalDataConsent from './PersonalDataConsent';
import MarketingConsent from './MarketingConsent';
import {
  optionalQuestionSchema,
  QUESTION_LABEL,
  WORK_EMAIL_LABEL,
} from '../lib/formFieldSchemas';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const requestPriceSchema = z.object({
  contact_name: z.string().min(2, { message: 'Укажите ФИО' }),
  email: z.string().min(1, { message: 'Укажите рабочую почту' }).regex(emailRegex, {
    message: 'Введите корректный email',
  }),
  question: optionalQuestionSchema,
  consent: z.boolean().refine((val) => val === true, {
    message: 'Необходимо согласие на обработку персональных данных',
  }),
  marketingConsent: z.boolean().optional(),
});

const RequestPriceForm = ({
  open,
  onOpenChange,
  privacyPolicyLink: _privacyPolicyLink,
  productName,
  licenseName,
  calculatorData = null,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [captchaToken, setCaptchaToken] = React.useState('');
  const captchaRef = useRef(null);

  const defaultValues = useMemo(
    () => ({
      contact_name: '',
      email: '',
      question: '',
      consent: false,
      marketingConsent: false,
    }),
    [],
  );

  const form = useForm({
    resolver: zodResolver(requestPriceSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    } else {
      setCaptchaToken('');
    }
  }, [open, form, defaultValues]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      if (!captchaToken) {
        alert(DDG_CAPTCHA_REQUIRED_MESSAGE);
        return;
      }
      await calculationAPI.createCalculationRequest(
        data.email.trim(),
        'email',
        calculatorData,
        data.contact_name.trim(),
        window.location.href,
        data.consent,
        data.marketingConsent,
        data.question?.trim() || null,
        captchaToken,
      );

      alert('Спасибо! Запрос отправлен. Мы свяжемся с вами по указанному email.');
      form.reset(defaultValues);
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting price request:', error);
      alert(error.message || 'Произошла ошибка. Попробуйте позже.');
      captchaRef.current?.reset();
      setCaptchaToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contextLabel = [productName, licenseName].filter(Boolean).join(' — ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-accent" />
            Запросить цену
          </DialogTitle>
          <DialogDescription>
            {contextLabel
              ? `Оставьте контакты — мы подготовим предложение по «${contextLabel}».`
              : 'Оставьте контакты — мы подготовим предложение по выбранной лицензии.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="contact_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ФИО *</FormLabel>
                  <FormControl>
                    <Input placeholder="Иванов Иван Иванович" autoComplete="name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{WORK_EMAIL_LABEL} *</FormLabel>
                  <FormControl>
                    <Input placeholder="name@company.ru" type="email" autoComplete="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{QUESTION_LABEL}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Кратко опишите задачу или вопрос"
                      className="min-h-[88px] resize-y"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="consent"
              render={({ field }) => (
                <FormItem>
                  <PersonalDataConsent
                    id="price-pd-consent"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    error={form.formState.errors.consent?.message}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="marketingConsent"
              render={({ field }) => (
                <FormItem>
                  <MarketingConsent
                    id="price-marketing-consent"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            <DdgCaptcha ref={captchaRef} onToken={setCaptchaToken} />

            <Button type="submit" className="w-full" disabled={isSubmitting || !captchaToken}>
              {isSubmitting ? 'Отправка...' : 'Отправить запрос'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestPriceForm;
